/**
 * Cardiac / Fitness Age estimator.
 *
 * Maps resting heart rate + heart rate variability (relative to age-
 * adjusted population norms) onto a biological cardiovascular age.
 *
 * Methodology (transparent + simple):
 *
 *   For each metric we compute a z-score against age-adjusted norms:
 *
 *     expected_HRV(age) = 50 - (age - 20) × 0.5  ms   // declines ~0.5ms/yr after 20
 *     expected_RHR(age) = 65 + (age - 20) × 0.1  bpm  // slowly rises with age
 *
 *   The user's deviation from those norms shifts their cardiac age:
 *
 *     HRV_shift =  ((user_HRV  - expected_HRV) / 10)  × 5 years
 *     RHR_shift = -((user_RHR  - expected_RHR) /  5)  × 5 years
 *
 *   Total shift = (HRV_shift + RHR_shift) / 2 — average the two signals
 *   so neither dominates. We clamp the result to ±15 years off the
 *   chronological age (physiological plausibility).
 *
 * This is not a clinical assessment. It's a single trackable KPI that
 * climbs UP when the user's cardiovascular fitness improves (RHR
 * down, HRV up). Surfaced on SITREP / metrics.
 *
 * Pure computation — no I/O. Caller passes baselines + age.
 */

export interface CardiacAgeResult {
  /** Estimated biological cardiovascular age (years). */
  cardiacAge: number;
  /** Years YOUNGER than chronological — positive = younger, negative = older. */
  shift: number;
  /** Per-component contribution for UI breakdown. */
  contributors: Array<{
    metric: 'hrv' | 'rhr';
    userValue: number;
    expected: number;
    deltaYears: number;   // signed; positive = makes you younger
    direction: 'younger' | 'older' | 'on-track';
  }>;
  /** 0-1 confidence — climbs with baseline sample count. */
  confidence: number;
  /** Plain-English summary. */
  headline: string;
}

const EXPECTED_HRV_AT_20 = 50;
const HRV_DECLINE_PER_YEAR = 0.5;
const EXPECTED_RHR_AT_20 = 65;
const RHR_RISE_PER_YEAR = 0.1;
const MAX_SHIFT_YEARS = 15;

export interface ComputeCardiacAgeInput {
  /** User's chronological age. */
  chronologicalAge: number;
  /** User's HRV baseline (30-day mean in ms). Null when insufficient
   *  wearable data. */
  hrvBaseline: number | null;
  /** Sample count behind the HRV baseline — drives confidence. */
  hrvSamples: number;
  /** User's RHR baseline (30-day mean in bpm). Null when insufficient. */
  rhrBaseline: number | null;
  /** Sample count behind RHR baseline. */
  rhrSamples: number;
}

function expectedHrvForAge(age: number): number {
  return Math.max(20, EXPECTED_HRV_AT_20 - (age - 20) * HRV_DECLINE_PER_YEAR);
}
function expectedRhrForAge(age: number): number {
  return EXPECTED_RHR_AT_20 + (age - 20) * RHR_RISE_PER_YEAR;
}

export function computeCardiacAge(input: ComputeCardiacAgeInput): CardiacAgeResult | null {
  const { chronologicalAge, hrvBaseline, hrvSamples, rhrBaseline, rhrSamples } = input;
  if (chronologicalAge < 15 || chronologicalAge > 90) return null;
  if (hrvBaseline === null && rhrBaseline === null) return null;

  const expectedHrv = expectedHrvForAge(chronologicalAge);
  const expectedRhr = expectedRhrForAge(chronologicalAge);

  const contributors: CardiacAgeResult['contributors'] = [];
  let totalShift = 0;
  let activeSignals = 0;
  let totalConf = 0;

  if (hrvBaseline !== null && hrvBaseline > 0 && hrvSamples >= 7) {
    // HRV higher than expected → younger (negative deltaYears = improvement
    // since we subtract from chronological age, but for UI we express
    // it as "you're X years younger" so positive = younger).
    const hrvDeltaYears = ((hrvBaseline - expectedHrv) / 10) * 5;
    contributors.push({
      metric: 'hrv',
      userValue: Math.round(hrvBaseline),
      expected: Math.round(expectedHrv),
      deltaYears: Math.round(hrvDeltaYears * 10) / 10,
      direction: hrvDeltaYears > 0.5 ? 'younger' : hrvDeltaYears < -0.5 ? 'older' : 'on-track',
    });
    totalShift += hrvDeltaYears;
    activeSignals++;
    // Confidence from sample size: 7 samples = 0.5, 21+ = 1.0
    totalConf += Math.min(1, (hrvSamples - 7) / 14 * 0.5 + 0.5);
  }

  if (rhrBaseline !== null && rhrBaseline > 0 && rhrSamples >= 7) {
    // RHR lower than expected → younger. Sign flipped so positive = younger.
    const rhrDeltaYears = -((rhrBaseline - expectedRhr) / 5) * 5;
    contributors.push({
      metric: 'rhr',
      userValue: Math.round(rhrBaseline),
      expected: Math.round(expectedRhr),
      deltaYears: Math.round(rhrDeltaYears * 10) / 10,
      direction: rhrDeltaYears > 0.5 ? 'younger' : rhrDeltaYears < -0.5 ? 'older' : 'on-track',
    });
    totalShift += rhrDeltaYears;
    activeSignals++;
    totalConf += Math.min(1, (rhrSamples - 7) / 14 * 0.5 + 0.5);
  }

  if (activeSignals === 0) return null;

  const rawShift = totalShift / activeSignals;
  const shift = Math.max(-MAX_SHIFT_YEARS, Math.min(MAX_SHIFT_YEARS, rawShift));
  const cardiacAge = Math.round((chronologicalAge - shift) * 10) / 10;
  const confidence = totalConf / activeSignals;

  let headline: string;
  if (shift >= 5) headline = `Your cardiovascular system reads ${Math.round(shift)} years younger than ${chronologicalAge}.`;
  else if (shift >= 1.5) headline = `You're trending younger — ${Math.round(shift * 10) / 10} years off your baseline age.`;
  else if (shift >= -1.5) headline = `On track for your age — cardiovascular markers in line with chronological.`;
  else if (shift >= -5) headline = `Slight aging signal — ${Math.abs(Math.round(shift * 10) / 10)} years older than chronological.`;
  else headline = `Cardiovascular markers ${Math.abs(Math.round(shift))} years older than chronological — focus on Z2 + sleep.`;

  return { cardiacAge, shift: Math.round(shift * 10) / 10, contributors, confidence, headline };
}

/**
 * Convenience loader: pulls user age + PersonalState baselines and
 * returns the cardiac age. Null when insufficient data.
 */
export async function loadCardiacAge(userId: string): Promise<CardiacAgeResult | null> {
  try {
    // Age lookup
    const { doc, getDoc } = await import('firebase/firestore');
    const { db } = await import('./firebase');
    const snap = await getDoc(doc(db, 'users', userId));
    const data = snap.exists() ? snap.data() : null;
    const chronologicalAge = typeof data?.age === 'number' && data.age > 0 ? data.age : null;
    if (!chronologicalAge) return null;

    // Pull PersonalState for the baselines + sample counts
    const { loadPersonalState } = await import('./personalState');
    const state = await loadPersonalState(userId, chronologicalAge);
    return computeCardiacAge({
      chronologicalAge,
      hrvBaseline: state.streams.hrv.baselineMean,
      hrvSamples: state.streams.hrv.baselineSamples,
      rhrBaseline: state.streams.rhr.baselineMean,
      rhrSamples: state.streams.rhr.baselineSamples,
    });
  } catch (e) {
    console.warn('[cardiac-age] load failed:', e);
    return null;
  }
}
